import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Text, Icon, type IconSource } from '@/lib/polaris';
import {
  StoreIcon,
  BankIcon,
  StarIcon,
  ProfileIcon,
  CreditCardIcon,
  CartIcon,
  DeliveryIcon,
  DomainIcon,
  ColorIcon,
  NotificationIcon,
  ChatIcon,
  AppsIcon,
  ConnectIcon,
  DataPresentationIcon,
  LocationIcon,
  GlobeIcon,
  ContractIcon,
  LockIcon,
  ChevronRightIcon,
  SearchIcon,
} from '@/lib/polaris-icons';
import { useAuth } from '@smarty/shared';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { ROUTES, storePath } from '@/config/routes.config';
import { SETTINGS_NAV } from '@/config/navigation.config';
import styles from './SettingsNav.module.css';

type SubItem = { label: string; route: string };
type NavItem = { label: string; route: string; icon: IconSource; exact?: boolean; disabled?: boolean; subItems?: SubItem[] };
type NavGroup = { title?: string; items: NavItem[] };

// Sub-ítems reusando la fuente de verdad (navigation.config). El padre "Envíos y entrega"
// apunta a la ruta base, que redirige al primer sub-ítem (Métodos de entrega): ya no hay
// página "Resumen" (era redundante con este mismo submenú).
const usersSubs: SubItem[] = SETTINGS_NAV.usersSubItems.map((i) => ({ label: i.label, route: i.url }));
const shippingSubs: SubItem[] = SETTINGS_NAV.shippingSubItems.map((i) => ({ label: i.label, route: i.url }));

const GROUPS: NavGroup[] = [
  {
    title: 'Cuenta',
    items: [
      { label: 'General', route: ROUTES.SETTINGS_GENERAL, icon: StoreIcon },
      { label: 'Empresa y cuentas', route: ROUTES.SETTINGS_BUSINESS, icon: BankIcon },
      { label: 'Plan y facturación', route: ROUTES.SETTINGS_PLAN, icon: StarIcon },
      { label: 'Usuarios', route: ROUTES.SETTINGS_USERS, icon: ProfileIcon, subItems: usersSubs },
    ],
  },
  {
    title: 'Tienda',
    items: [
      { label: 'Pagos', route: ROUTES.SETTINGS_PAYMENTS, icon: CreditCardIcon },
      { label: 'Checkout', route: ROUTES.SETTINGS_CHECKOUT, icon: CartIcon },
      { label: 'Envíos y entrega', route: ROUTES.SETTINGS_SHIPPING, icon: DeliveryIcon, subItems: shippingSubs },
      { label: 'Dominios', route: ROUTES.SETTINGS_DOMAINS, icon: DomainIcon },
      { label: 'Marca', route: ROUTES.SETTINGS_BRAND, icon: ColorIcon },
      { label: 'Notificaciones', route: ROUTES.SETTINGS_NOTIFICATIONS, icon: NotificationIcon },
      // Va junto a Notificaciones y no en «Canales»: gobierna los avisos al CLIENTE de todos los
      // módulos, no la configuración de un canal. ADR-0011.
      { label: 'Contacto con clientes', route: ROUTES.SETTINGS_CONTACT_POLICY, icon: NotificationIcon },
    ],
  },
  {
    title: 'Canales y apps',
    items: [
      { label: 'Apps y canales', route: ROUTES.SETTINGS_APPS_AND_SALES_CHANNELS, icon: AppsIcon },
      { label: 'Integraciones', route: ROUTES.SETTINGS_INTEGRATIONS, icon: ConnectIcon },
      { label: 'Notificaciones por WhatsApp', route: ROUTES.SETTINGS_NOTIFICATIONS_WHATSAPP, icon: ChatIcon },
    ],
  },
  {
    title: 'Avanzado',
    items: [
      { label: 'Privacidad del cliente', route: '/settings/privacy', icon: LockIcon },
      { label: 'Políticas', route: '/settings/policies', icon: ContractIcon },
      { label: 'Facturación', route: ROUTES.SETTINGS_BILLING, icon: ContractIcon, disabled: true },
      { label: 'Impuestos y aranceles', route: '/settings/taxes-and-duties', icon: DataPresentationIcon, disabled: true },
      { label: 'Sucursales', route: '/settings/locations', icon: LocationIcon, disabled: true },
      { label: 'Idiomas', route: '/settings/languages', icon: GlobeIcon, disabled: true },
    ],
  },
];

export function SettingsNav() {
  const location = useLocation();
  const storeNavigate = useStoreNavigate();
  const { storeSlug, activeTenant, user } = useAuth();
  const currentPath = location.pathname || '';
  const [manualOpen, setManualOpen] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const filteredGroups: NavGroup[] = q
    ? GROUPS.map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            it.label.toLowerCase().includes(q) ||
            it.subItems?.some((s) => s.label.toLowerCase().includes(q)),
        ),
      })).filter((g) => g.items.length > 0)
    : GROUPS;

  const fullUrl = (route: string) => (storeSlug ? storePath(storeSlug, route) : route);
  const pathMatches = (route: string, exact = false) => {
    const full = fullUrl(route);
    return exact ? currentPath === full : currentPath === full || currentPath.startsWith(`${full}/`);
  };

  const storeName = activeTenant?.name || storeSlug || 'Mi tienda';
  const storeInitial = storeName.charAt(0).toUpperCase();
  const userName =
    (user?.user_metadata?.full_name as string | undefined) || user?.email?.split('@')[0] || 'Cuenta';
  const userEmail = user?.email ?? '';
  const userInitial = userName.charAt(0).toUpperCase();

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.storeAvatar}>{storeInitial}</span>
        <span className={styles.headerText}>
          <Text as="span" variant="bodyMd" fontWeight="semibold" truncate>{storeName}</Text>
          {storeSlug ? <Text as="span" variant="bodySm" tone="subdued" truncate>{storeSlug}</Text> : null}
        </span>
      </div>

      <div className={styles.search}>
        <span className={styles.searchIcon}><Icon source={SearchIcon} tone="subdued" /></span>
        <input
          type="search"
          name="settings-nav-search"
          autoComplete="off"
          className={styles.searchInput}
          placeholder="Buscar"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Buscar en configuración"
        />
      </div>

      <nav className={styles.list} aria-label="Configuración">
        {filteredGroups.length === 0 ? (
          <div className={styles.empty}>
            <Text as="span" variant="bodySm" tone="subdued">Sin resultados para "{query}"</Text>
          </div>
        ) : null}
        {filteredGroups.map((group, gi) => (
          <div key={group.title ?? `g${gi}`}>
            {group.title ? <div className={styles.groupLabel}>{group.title}</div> : null}
            {group.items.map((item) => {
              const hasSubs = !!item.subItems?.length;
              const selfActive = !item.disabled && pathMatches(item.route, hasSubs || item.exact);
              const subActive = hasSubs ? item.subItems!.some((s) => pathMatches(s.route)) : false;
              const open = hasSubs ? (q ? true : (manualOpen[item.route] ?? (selfActive || subActive))) : false;
              const rowCls = [styles.item, selfActive && !subActive ? styles.active : '', item.disabled ? styles.disabled : '']
                .filter(Boolean)
                .join(' ');
              return (
                <div key={item.route}>
                  <div className={rowCls}>
                    <button
                      type="button"
                      className={styles.itemMain}
                      aria-current={selfActive && !subActive ? 'page' : undefined}
                      aria-disabled={item.disabled || undefined}
                      onClick={item.disabled ? undefined : () => storeNavigate(item.route)}
                    >
                      <span className={styles.icon}><Icon source={item.icon} tone="inherit" /></span>
                      <span className={styles.label}>{item.label}</span>
                    </button>
                    {hasSubs ? (
                      <button
                        type="button"
                        className={styles.chevBtn}
                        aria-label={open ? `Contraer ${item.label}` : `Expandir ${item.label}`}
                        aria-expanded={open}
                        onClick={() =>
                          setManualOpen((m) => ({ ...m, [item.route]: !(m[item.route] ?? (selfActive || subActive)) }))
                        }
                      >
                        <span className={`${styles.chev} ${open ? styles.chevOpen : ''}`}>
                          <Icon source={ChevronRightIcon} tone="inherit" />
                        </span>
                      </button>
                    ) : null}
                  </div>
                  {hasSubs && open ? (
                    <div>
                      {item.subItems!.map((sub) => {
                        const sActive = pathMatches(sub.route);
                        return (
                          <button
                            key={sub.route}
                            type="button"
                            className={`${styles.sub} ${sActive ? styles.active : ''}`}
                            aria-current={sActive ? 'page' : undefined}
                            onClick={() => storeNavigate(sub.route)}
                          >
                            {sub.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div className={styles.account}>
        <span className={styles.accountAvatar}>{userInitial}</span>
        <span className={styles.accountText}>
          <Text as="span" variant="bodySm" fontWeight="medium" truncate>{userName}</Text>
          {userEmail ? <Text as="span" variant="bodySm" tone="subdued" truncate>{userEmail}</Text> : null}
        </span>
      </div>
    </div>
  );
}
